import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import pedirSenhaAdmin from '../components/common/pedirSenhaAdmin';
import { abrirDanfeNfePdf, gerarDanfeNfceVenda, abrirDanfseOficial } from '../utils/impressaoVenda';
import AbaMdfe from '../components/fiscal/AbaMdfe';
import AbaContador from '../components/fiscal/AbaContador';
import s from './Financeiro.module.css';
import InputDataFiltro from '../components/common/InputDataFiltro';

const fmtMoeda = v => v == null ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDT    = d => d ? new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';
const isoHoje    = () => new Date().toLocaleDateString('en-CA');
const isoIniMes  = () => isoHoje().slice(0, 8) + '01';

const STATUS_BADGE = {
  autorizada: s.badgeAtivo,
  pendente:   s.badgeCofre,
  rejeitada:  s.badgeInativo,
  cancelada:  s.badgeOutros,
  epec:       s.badgeCofre, // contingência aguardando transmissão (168h)
  contingencia: s.badgeCofre, // NFC-e off-line aguardando transmissão
  contingencia_rejeitada: s.badgeInativo,
};

function ModalEmail({ titulo, emailPadrao, onEnviar, onClose }) {
  const [email, setEmail]         = useState(emailPadrao || '');
  const [enviando, setEnviando]   = useState(false);
  const [erro, setErro]           = useState('');

  const confirmar = async () => {
    if (!email.trim()) { setErro('Informe o e-mail de destino.'); return; }
    setErro(''); setEnviando(true);
    try {
      await onEnviar(email.trim());
      onClose();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao enviar e-mail.');
    } finally { setEnviando(false); }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'var(--surface, #fff)', borderRadius: 8, padding: 20, width: 420 }}>
        <div style={{ fontWeight: 'bold', marginBottom: 12 }}>{titulo}</div>
        <label className={s.label}>E-mail de destino</label>
        <input className={`${s.input} ${s.inputLarge}`} value={email}
          onChange={e => setEmail(e.target.value)} placeholder="contador@escritorio.com.br" autoFocus />
        {erro && <div className={s.erroMsg} style={{ marginTop: 8 }}>{erro}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className={s.btnErp} onClick={onClose}>Cancelar</button>
          <button className={s.btnErpPrimario} onClick={confirmar} disabled={enviando}>
            {enviando ? 'Enviando...' : '✉ Enviar'}
          </button>
        </div>
      </div>
    </div>
  );
}

const ROTULO_TIPO = { nfce: 'NFC-e', nfe: 'NF-e', nfse: 'NFS-e' };

function ModalInutilizacao({ tipo, onClose }) {
  const [faixas, setFaixas]       = useState([]);
  const [historico, setHistorico] = useState([]);
  const [ini, setIni]             = useState('');
  const [fim, setFim]             = useState('');
  const [just, setJust]           = useState('');
  const [erro, setErro]           = useState('');
  const [msg, setMsg]             = useState('');
  const [enviando, setEnviando]   = useState(false);

  const carregar = useCallback(async () => {
    try {
      const [p, h] = await Promise.all([
        api.get('/fiscal/inutilizacoes/pendencias', { params: { tipo } }),
        api.get('/fiscal/inutilizacoes', { params: { tipo } }),
      ]);
      setFaixas(p.data.faixas || []);
      setHistorico(h.data || []);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao carregar dados de inutilização.');
    }
  }, [tipo]);
  useEffect(() => { carregar(); }, [carregar]);

  const inutilizar = async () => {
    setErro(''); setMsg('');
    const nIni = parseInt(ini), nFim = parseInt(fim);
    if (!nIni || !nFim || nFim < nIni) { setErro('Informe a faixa (número inicial ≤ final).'); return; }
    if (just.trim().length < 15) { setErro('A justificativa deve ter no mínimo 15 caracteres.'); return; }
    const senha = await pedirSenhaAdmin(
      `Inutilizar a numeração ${ROTULO_TIPO[tipo]} nº ${nIni} a ${nFim} na SEFAZ. A operação é IRREVERSÍVEL — esses números nunca mais poderão ser usados.`,
      { acao: 'inutilizar_numeracao', detalhes: `${ROTULO_TIPO[tipo]} série 1 nº ${nIni} a ${nFim}` }
    );
    if (!senha) return;
    setEnviando(true);
    try {
      const { data } = await api.post('/fiscal/inutilizacoes', {
        tipo, numero_inicial: nIni, numero_final: nFim,
        justificativa: just.trim(), senha_admin: senha,
      });
      if (data.sucesso) {
        setMsg(`Faixa nº ${nIni} a ${nFim} inutilizada — protocolo ${data.protocolo}.`);
        setIni(''); setFim(''); setJust('');
      } else {
        setErro(`SEFAZ recusou (cStat ${data.cStat}): ${data.motivo}`);
      }
      carregar();
    } catch (err) {
      setErro(err.response?.data?.erro || err.response?.data?.motivo || 'Erro ao transmitir a inutilização.');
    } finally { setEnviando(false); }
  };

  const usarFaixa = f => { setIni(String(f.numero_inicial)); setFim(String(f.numero_final)); setErro(''); setMsg(''); };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'var(--surface, #fff)', borderRadius: 8, padding: 20, width: 640, maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ fontWeight: 'bold', marginBottom: 6 }}>🚫 Inutilizar numeração — {ROTULO_TIPO[tipo]} (série 1)</div>
        <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
          Declara à SEFAZ que uma faixa de números não será usada (buracos na sequência).
          Números com nota autorizada, cancelada ou já existente na SEFAZ não podem ser inutilizados.
          Atenção: número rejeitado por erro sanável é <b>reaproveitado automaticamente</b> pela próxima
          emissão — inutilize apenas o que não será mesmo reutilizado (ex.: fechamento de mês/ano para o contador).
        </div>

        <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 4 }}>Buracos detectados na numeração</div>
        {faixas.length === 0 && <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>Nenhum buraco pendente 🎉</div>}
        {faixas.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            {faixas.map((f, i) => (
              <button key={i} className={s.btnErp} style={{ height: 24, fontSize: 12 }} onClick={() => usarFaixa(f)}>
                {f.numero_inicial === f.numero_final ? `nº ${f.numero_inicial}` : `nº ${f.numero_inicial} a ${f.numero_final}`}
              </button>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 8 }}>
          <label style={{ fontSize: 11, display: 'flex', flexDirection: 'column', gap: 2 }}>
            Número inicial
            <input type="number" min="1" className={s.input} style={{ width: 110 }} value={ini} onChange={e => setIni(e.target.value)} />
          </label>
          <label style={{ fontSize: 11, display: 'flex', flexDirection: 'column', gap: 2 }}>
            Número final
            <input type="number" min="1" className={s.input} style={{ width: 110 }} value={fim} onChange={e => setFim(e.target.value)} />
          </label>
          <button className={s.btnErpPrimario} onClick={inutilizar} disabled={enviando}>
            {enviando ? 'Transmitindo...' : '🚫 Inutilizar na SEFAZ'}
          </button>
        </div>
        <label className={s.label}>Justificativa (mín. 15 caracteres)</label>
        <textarea className={s.input} style={{ width: '100%', minHeight: 52, resize: 'vertical', boxSizing: 'border-box' }}
          value={just} onChange={e => setJust(e.target.value)}
          placeholder="Ex.: Numeração pulada por falha na transmissão — números não utilizados." />

        {erro && <div className={s.erroMsg} style={{ marginTop: 8 }}>{erro}</div>}
        {msg  && <div className={s.sucessoMsg} style={{ marginTop: 8 }}>{msg}</div>}

        <div style={{ fontSize: 12, fontWeight: 'bold', margin: '14px 0 4px' }}>Inutilizações registradas</div>
        <table className={s.tabela}>
          <thead><tr>
            <th style={{ width: 105 }}>Data</th>
            <th style={{ width: 110 }}>Faixa</th>
            <th>Justificativa</th>
            <th style={{ width: 90 }}>Status</th>
            <th style={{ width: 140 }}>Protocolo</th>
          </tr></thead>
          <tbody>
            {historico.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: '#888', padding: 10 }}>Nenhuma inutilização registrada.</td></tr>
            )}
            {historico.map(h => (
              <tr key={h.id}>
                <td>{fmtDT(h.criado_em)}</td>
                <td>{h.numero_inicial === h.numero_final ? `nº ${h.numero_inicial}` : `${h.numero_inicial} a ${h.numero_final}`}</td>
                <td title={h.justificativa}>{h.justificativa}</td>
                <td><span className={`${s.badge} ${h.status === 'homologada' ? s.badgeAtivo : s.badgeInativo}`}>{h.status}</span></td>
                <td>{h.protocolo || (h.cstat ? `cStat ${h.cstat}` : '—')}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
          <button className={s.btnErp} onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

export default function ModuloFiscal() {
  const { usuario } = useAuth();
  const [aba, setAba] = useState('nfce'); // nfce | nfe | compras | nfse | rejeicoes | mdfe | contador

  // NFS-e (Sistema Nacional) — ainda só diagnóstico/consulta, sem emissão
  const [nfseTestando, setNfseTestando] = useState(false);
  const [nfseResultado, setNfseResultado] = useState(null);
  const [nfseErro, setNfseErro] = useState('');
  const [nfseCodServico, setNfseCodServico] = useState('');
  const testarNfse = async () => {
    setNfseTestando(true); setNfseErro(''); setNfseResultado(null);
    try {
      const { data } = await api.get('/fiscal/nfse/parametros-municipais', {
        params: nfseCodServico.trim() ? { codigo_servico: nfseCodServico.trim() } : {},
      });
      setNfseResultado(data);
    } catch (err) {
      setNfseErro(err.response?.data?.erro || 'Erro ao consultar o Sistema Nacional NFS-e.');
      if (err.response?.data) setNfseResultado(err.response.data);
    } finally {
      setNfseTestando(false);
    }
  };

  const [nfseProdutoId, setNfseProdutoId] = useState('');
  const [nfseCtribnac, setNfseCtribnac] = useState(null);
  const [nfseCtribnacErro, setNfseCtribnacErro] = useState('');
  const resolverCtribnac = async () => {
    setNfseCtribnacErro(''); setNfseCtribnac(null);
    if (!nfseProdutoId.trim()) return;
    try {
      const { data } = await api.get(`/fiscal/nfse/resolver-ctribnac/${nfseProdutoId.trim()}`);
      setNfseCtribnac(data);
    } catch (err) {
      setNfseCtribnacErro(err.response?.data?.erro || 'Erro ao resolver o código.');
    }
  };
  const [emailContador, setEmailContador] = useState('');

  // Vendas (abas nfce/nfe)
  const [docsVenda, setDocsVenda]   = useState([]);
  const [status, setStatus]         = useState('');
  const [busca, setBusca]           = useState('');
  const [carregandoVenda, setCarregandoVenda] = useState(false);

  // Compras
  const [docsCompra, setDocsCompra] = useState([]);
  const [carregandoCompra, setCarregandoCompra] = useState(false);

  // NFS-e
  const [docsNfse, setDocsNfse]       = useState([]);
  const [statusNfse, setStatusNfse]   = useState('');
  const [buscaNfse, setBuscaNfse]     = useState('');
  const [carregandoNfse, setCarregandoNfse] = useState(false);

  // Log de Rejeições — NF-e/NFC-e/NFS-e rejeitada não é documento fiscal de
  // fato (nunca existiu de verdade pra SEFAZ/Sistema Nacional), então fica
  // fora da Central de Notas de propósito; aqui é só auditoria/diagnóstico.
  const [docsRejeicoes, setDocsRejeicoes]         = useState([]);
  const [tipoRejeicoes, setTipoRejeicoes]         = useState('');
  const [buscaRejeicoes, setBuscaRejeicoes]       = useState('');
  const [carregandoRejeicoes, setCarregandoRejeicoes] = useState(false);
  const carregarRejeicoes = useCallback(async () => {
    setCarregandoRejeicoes(true); setErro('');
    try {
      const { data } = await api.get('/fiscal/documentos', {
        params: { status: 'rejeitada', tipo: tipoRejeicoes || undefined, busca: buscaRejeicoes || undefined },
      });
      setDocsRejeicoes(data);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao carregar o log de rejeições.');
    } finally { setCarregandoRejeicoes(false); }
  }, [tipoRejeicoes, buscaRejeicoes]);
  useEffect(() => { if (aba === 'rejeicoes') carregarRejeicoes(); }, [aba, carregarRejeicoes]);

  // Período: filtra a lista de notas de venda e as ações em lote
  const [de, setDe]   = useState(isoIniMes());
  const [ate, setAte] = useState(isoHoje());

  const [erro, setErro]     = useState('');
  const [aviso, setAviso]   = useState('');
  const [ocupadoId, setOcupadoId] = useState(null);
  const [processandoLote, setProcessandoLote] = useState(false);
  const [modalEmail, setModalEmail] = useState(null); // { titulo, onEnviar }
  const [modalInutilizar, setModalInutilizar] = useState(false);
  const [orgData, setOrgData] = useState({}); // header/logo do DANFE NFC-e/NFS-e reimpresso daqui

  useEffect(() => {
    if (!usuario?.organizacao_id) return;
    api.get(`/organizacoes/${usuario.organizacao_id}`)
      .then(({ data }) => { setEmailContador(data.email_contador || ''); setOrgData(data); })
      .catch(() => {});
  }, [usuario]);

  // "Gerar DANFE" na Central de Notas — reaproveita os mesmos 3 fluxos que já
  // existem no PDV/F7 (ver PDV.jsx#imprimirDanfeNfceMov e
  // ModalBuscarMovimentacao.jsx): NF-e tem PDF pronto no servidor; NFC-e é
  // montado no navegador a partir de danfe-dados + a venda; NFS-e busca o
  // PDF oficial do Sistema Nacional (com fallback pro facsímile local se o
  // governo estiver fora do ar). 'a4' de propósito (não thermal) — esta é a
  // tela de escritório/auditoria, não o balcão do PDV.
  const gerarDanfe = async (d) => {
    setOcupadoId(d.id);
    try {
      if (d.tipo === 'nfe') {
        await abrirDanfeNfePdf(`/fiscal/nfe/${d.id}/danfe`);
      } else if (d.tipo === 'nfce') {
        // Mesma sequência de PDV.jsx#imprimirDanfeNfceMov — gerarDanfeNfceVenda
        // abre sua própria aba internamente, já depois destes dois GETs.
        try {
          const [{ data: dados }, { data: v }] = await Promise.all([
            api.get(`/fiscal/documentos/${d.id}/danfe-dados`),
            api.get(`/vendas/${d.venda_id}`),
          ]);
          await gerarDanfeNfceVenda(v, dados, 'a4', orgData);
        } catch (err) {
          alert(err.response?.data?.erro || 'Erro ao montar o DANFE da NFC-e.');
        }
      } else if (d.tipo === 'nfse') {
        await abrirDanfseOficial({ documento_id: d.id, sucesso: true }, orgData);
      }
    } finally {
      setOcupadoId(null);
    }
  };

  // Cancelar documento direto da Central de Notas — mesma rotina que já
  // existe no F7 do PDV (PDV.jsx#cancelarDocFiscalMov): cancela só a NF-e/
  // NFC-e na SEFAZ (evento 110111), a venda em si permanece 'concluida' e
  // pode ser localizada de novo em Módulo Fiscal → Nota Fiscal → "De uma
  // Venda" pra uma nova emissão (pedido explícito, 2026-09-14 — homologação
  // só, aguardando validar antes de levar pros clientes reais).
  const cancelarDocDaLista = async (d) => {
    const tipoDoc = d.tipo === 'nfe' ? 'NF-e' : 'NFC-e';
    const motivoPadrao = orgData?.[d.tipo]?.motivo_cancelamento_padrao || '';
    const just = window.prompt(
      `Cancelar a ${tipoDoc} nº ${d.numero}${d.serie ? `/${d.serie}` : ''} na SEFAZ?\n\n` +
      `A venda em si não é desfeita — fica disponível pra emitir outra nota depois.\n` +
      `Informe a justificativa (mínimo 15 caracteres):`,
      motivoPadrao
    );
    if (just === null) return;
    if (just.trim().length < 15) { alert('Justificativa deve ter no mínimo 15 caracteres.'); return; }
    const senha = await pedirSenhaAdmin(
      `Cancelar ${tipoDoc} nº ${d.numero}${d.serie ? `/${d.serie}` : ''} na SEFAZ.`,
      { acao: 'cancelar_documento_fiscal', detalhes: `${tipoDoc} nº ${d.numero} — venda #${d.venda_id}. Justificativa: ${just.trim()}` }
    );
    if (!senha) return;
    setOcupadoId(d.id);
    try {
      const { data } = await api.post(`/fiscal/documentos/${d.id}/cancelar`, {
        justificativa: just.trim(), senha_admin: senha,
      });
      if (data.sucesso) {
        setAviso(`${tipoDoc} nº ${d.numero} cancelada na SEFAZ. Protocolo: ${data.protocolo}${data.fora_do_prazo ? ' (registrado fora do prazo)' : ''}`);
        setDocsVenda(prev => prev.map(x => x.id === d.id ? { ...x, status: 'cancelada' } : x));
      } else {
        setErro(`Cancelamento não homologado: ${data.motivo || ''} (cStat ${data.cStat || '—'})`);
      }
    } catch (err) {
      setErro(err.response?.data?.erro || err.response?.data?.motivo || 'Erro ao cancelar documento.');
    } finally {
      setOcupadoId(null);
    }
  };

  const abaVendas = aba === 'nfce' || aba === 'nfe';

  const carregarVendas = useCallback(async () => {
    setCarregandoVenda(true); setErro('');
    try {
      const { data } = await api.get('/fiscal/documentos', {
        // O período também filtra a LISTA (antes só valia para zip/e-mail em
        // lote, e a lista mostrava sempre as 500 mais recentes).
        params: { tipo: aba, status: status || undefined, busca: busca || undefined, de: de || undefined, ate: ate || undefined },
      });
      setDocsVenda(data);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao carregar notas de venda.');
    } finally { setCarregandoVenda(false); }
  }, [aba, status, busca, de, ate]);

  const carregarCompras = useCallback(async () => {
    setCarregandoCompra(true); setErro('');
    try {
      const { data } = await api.get('/dfe/documentos');
      setDocsCompra((data.documentos || []).filter(d => d.tipo === 'nfe'));
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao carregar notas de compra.');
    } finally { setCarregandoCompra(false); }
  }, []);

  const carregarNfse = useCallback(async () => {
    setCarregandoNfse(true); setErro('');
    try {
      const { data } = await api.get('/fiscal/documentos', {
        params: { tipo: 'nfse', status: statusNfse || undefined, busca: buscaNfse || undefined },
      });
      setDocsNfse(data);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao carregar notas de serviço.');
    } finally { setCarregandoNfse(false); }
  }, [statusNfse, buscaNfse]);

  useEffect(() => { if (abaVendas) carregarVendas(); }, [abaVendas, carregarVendas]);
  useEffect(() => { if (aba === 'compras') carregarCompras(); }, [aba, carregarCompras]);
  useEffect(() => { if (aba === 'nfse') carregarNfse(); }, [aba, carregarNfse]);

  const baixarBlob = (resp, nomePadrao) => {
    const nome = (resp.headers['content-disposition'] || '').match(/filename="(.+)"/)?.[1] || nomePadrao;
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement('a');
    a.href = url; a.download = nome; a.click();
    URL.revokeObjectURL(url);
  };

  /* EPEC: transmite a NF-e da contingência (conciliação em até 168h) */
  const transmitirEpecDoc = async (doc) => {
    if (!window.confirm(`Transmitir agora a NF-e nº ${doc.numero} emitida em contingência (EPEC)?`)) return;
    setOcupadoId(doc.id);
    try {
      const { data } = await api.post(`/fiscal/epec/${doc.id}/transmitir`);
      alert(`${data.sucesso ? '✔' : '✘'} ${data.status} — ${data.motivo}\n${data.aviso || ''}`);
      carregarVendas();
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao transmitir a NF-e do EPEC.');
    } finally { setOcupadoId(null); }
  };

  const baixarXmlVenda = async (doc) => {
    setErro(''); setOcupadoId(doc.id);
    try {
      const { data } = await api.get(`/fiscal/documentos/${doc.id}/xml`);
      const blob = new Blob([data.xml], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `NFe${(doc.chave_acesso || doc.numero).replace(/\D/g, '') || doc.numero}.xml`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao obter o XML.');
    } finally { setOcupadoId(null); }
  };

  const baixarXmlCompra = async (doc) => {
    setErro(''); setOcupadoId(doc.id);
    try {
      const { data } = await api.get(`/dfe/documentos/${doc.id}/xml`);
      const blob = new Blob([data.xml], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `NFe${(doc.chave_acesso || '').replace(/\D/g, '') || doc.id}.xml`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao obter o XML.');
    } finally { setOcupadoId(null); }
  };

  const enviarEmailVenda = (doc) => setModalEmail({
    titulo: `Enviar XML da nota nº ${doc.numero} por e-mail`,
    onEnviar: async (email) => {
      await api.post(`/fiscal/documentos/${doc.id}/enviar-email`, { email });
      setAviso(`XML da nota nº ${doc.numero} enviado para ${email}.`);
    },
  });

  const enviarEmailCompra = (doc) => setModalEmail({
    titulo: `Enviar XML da compra de ${doc.nome_emitente || 'fornecedor'} por e-mail`,
    onEnviar: async (email) => {
      await api.post(`/dfe/documentos/${doc.id}/enviar-email`, { email });
      setAviso(`XML da compra de ${doc.nome_emitente || 'fornecedor'} enviado para ${email}.`);
    },
  });

  const baixarZipLote = async () => {
    setErro(''); setAviso(''); setProcessandoLote(true);
    try {
      const url = abaVendas ? '/fiscal/documentos/xmls-zip' : '/dfe/xmls-zip';
      const params = abaVendas ? { de, ate, tipo: aba } : { de, ate };
      const resp = await api.get(url, { params, responseType: 'blob' });
      baixarBlob(resp, `xmls-${aba}_${de}_a_${ate}.zip`);
    } catch (err) {
      let msg = 'Erro ao baixar os XMLs.';
      if (err.response?.data instanceof Blob) {
        try { msg = JSON.parse(await err.response.data.text()).erro || msg; } catch (_) {}
      } else msg = err.response?.data?.erro || msg;
      setErro(msg);
    } finally { setProcessandoLote(false); }
  };

  const rotuloAba = { nfce: 'venda (NFC-e)', nfe: 'venda (NF-e)', compras: 'compras' }[aba];

  const enviarEmailLote = () => setModalEmail({
    titulo: `Enviar por e-mail todos os XMLs de ${rotuloAba} — ${de} a ${ate}`,
    onEnviar: async (email) => {
      const url = abaVendas ? '/fiscal/documentos/enviar-email-lote' : '/dfe/xmls-zip/enviar-email';
      const body = abaVendas ? { de, ate, email, tipo: aba } : { de, ate, email };
      const { data } = await api.post(url, body);
      setAviso(`${data.total} nota(s) enviada(s) para ${email}.`);
    },
  });

  return (
    <div className={s.erpWrap}>
      <div className={s.titleBar}><span>🧾</span> Módulo Fiscal — Central de Notas</div>

      <div className={s.toolbar}>
        <button className={aba === 'nfce' ? s.btnErpPrimario : s.btnErp} onClick={() => setAba('nfce')}>
          Notas de Venda — NFC-e
        </button>
        <button className={aba === 'nfe' ? s.btnErpPrimario : s.btnErp} onClick={() => setAba('nfe')}>
          Notas de Venda — NF-e
        </button>
        <button className={aba === 'compras' ? s.btnErpPrimario : s.btnErp} onClick={() => setAba('compras')}>
          Notas de Compra (DF-e)
        </button>
        <button className={aba === 'nfse' ? s.btnErpPrimario : s.btnErp} onClick={() => setAba('nfse')}>
          Notas de Venda — NFS-e
        </button>
        <button className={aba === 'rejeicoes' ? s.btnErpPrimario : s.btnErp} onClick={() => setAba('rejeicoes')}
          title="Notas rejeitadas pela SEFAZ/Sistema Nacional — não são documento fiscal de fato, ficam aqui só pra auditoria/diagnóstico">
          🪵 Log de Rejeições
        </button>
        <button className={aba === 'mdfe' ? s.btnErpPrimario : s.btnErp} onClick={() => setAba('mdfe')}>
          🚚 MDF-e
        </button>
        <button className={aba === 'contador' ? s.btnErpPrimario : s.btnErp} onClick={() => setAba('contador')}
          title="Fechamento do mês: resumo, conferência e o pacote com todos os XMLs para o contador">
          📒 Área do Contador
        </button>
      </div>

      {erro    && <div className={s.erroMsg}>{erro}</div>}
      {aviso   && <div className={s.sucessoMsg}>{aviso}</div>}

      {abaVendas && (
        <>
          <div className={s.toolbar} style={{ flexWrap: 'wrap' }}>
            <select className={s.select} value={status} onChange={e => setStatus(e.target.value)}
              title="Notas rejeitadas não aparecem aqui — não são documento fiscal de fato; veja-as no Log de Rejeições">
              <option value="">Todos os status</option>
              <option value="autorizada">Autorizada</option>
              <option value="pendente">Pendente</option>
              <option value="cancelada">Cancelada</option>
            </select>
            <input className={s.input} style={{ flex: 1, minWidth: 200 }}
              placeholder="Filtrar por número, chave ou cliente..."
              value={busca} onChange={e => setBusca(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && carregarVendas()} />
            <button className={s.btnErp} onClick={carregarVendas}>🔍 Buscar</button>
          </div>

          <div className={s.toolbar}>
            <label style={{ fontSize: 11, display: 'flex', flexDirection: 'column', gap: 2 }}>
              Período — de
              <InputDataFiltro className={s.input} value={de} onChange={e => setDe(e.target.value)} />
            </label>
            <label style={{ fontSize: 11, display: 'flex', flexDirection: 'column', gap: 2 }}>
              até
              <InputDataFiltro className={s.input} value={ate} onChange={e => setAte(e.target.value)} />
            </label>
            <button className={s.btnErp} onClick={baixarZipLote} disabled={processandoLote}>
              {processandoLote ? 'Compactando...' : '🗜️ Baixar XMLs (.zip)'}
            </button>
            <button className={s.btnErp} onClick={enviarEmailLote} disabled={processandoLote}>
              ✉ Enviar todas por e-mail
            </button>
            <button className={s.btnErp} onClick={() => setModalInutilizar(true)}>
              🚫 Inutilizar numeração
            </button>
          </div>

          <table className={s.tabela}>
            <thead><tr>
              <th style={{ width: 110 }}>Emissão</th>
              <th style={{ width: 90 }}>Número</th>
              <th>Cliente</th>
              <th style={{ width: 100, textAlign: 'right' }}>Valor</th>
              <th style={{ width: 100 }}>Status</th>
              <th style={{ width: 260 }}>Ações</th>
            </tr></thead>
            <tbody>
              {carregandoVenda && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#888', padding: 14 }}>Carregando...</td></tr>}
              {!carregandoVenda && docsVenda.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: '#888', padding: 14 }}>Nenhuma nota encontrada.</td></tr>
              )}
              {docsVenda.map(d => (
                <tr key={d.id} title={d.chave_acesso || ''}>
                  <td>{fmtDT(d.data_emissao)}</td>
                  <td>{d.numero}{d.serie ? `/${d.serie}` : ''}</td>
                  <td>{d.cliente_nome || (d.devolucao_id ? 'Devolução de venda' : d.entrada_nf_id ? 'Devolução de compra' : '—')}</td>
                  <td style={{ textAlign: 'right' }}>{fmtMoeda(d.valor)}</td>
                  <td><span className={`${s.badge} ${STATUS_BADGE[d.status] || s.badgeOutros}`}>{d.status}</span></td>
                  <td>
                    {d.status === 'epec' && (
                      <button className={s.btnErp} style={{ height: 22, fontSize: 11, padding: '0 8px', marginRight: 4 }}
                        disabled={ocupadoId === d.id}
                        title="Transmite a NF-e da contingência EPEC à SEFAZ (prazo: 168h após o registro)"
                        onClick={() => transmitirEpecDoc(d)}>
                        {ocupadoId === d.id ? '...' : '📡 Transmitir'}
                      </button>
                    )}
                    {(d.status === 'autorizada' || d.status === 'cancelada') && (
                      <button className={s.btnErp} style={{ height: 22, fontSize: 11, padding: '0 8px', marginRight: 4 }}
                        disabled={ocupadoId === d.id} onClick={() => gerarDanfe(d)}>
                        {ocupadoId === d.id ? '...' : '🖨️ DANFE'}
                      </button>
                    )}
                    {d.status === 'autorizada' && !d.devolucao_id && !d.entrada_nf_id && (
                      <button className={s.btnErpPerigo} style={{ height: 22, fontSize: 11, padding: '0 8px', marginRight: 4 }}
                        disabled={ocupadoId === d.id}
                        title="Cancela só a nota na SEFAZ — a venda continua disponível pra emitir outra depois"
                        onClick={() => cancelarDocDaLista(d)}>
                        {ocupadoId === d.id ? '...' : '✕ Cancelar'}
                      </button>
                    )}
                    {d.tem_xml && (
                      <>
                        <button className={s.btnErp} style={{ height: 22, fontSize: 11, padding: '0 8px' }}
                          disabled={ocupadoId === d.id} onClick={() => baixarXmlVenda(d)}>
                          {ocupadoId === d.id ? '...' : '⬇ XML'}
                        </button>
                        <button className={s.btnErp} style={{ height: 22, fontSize: 11, padding: '0 8px', marginLeft: 4 }}
                          onClick={() => enviarEmailVenda(d)}>
                          ✉ E-mail
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {aba === 'compras' && (
        <>
          <div className={s.toolbar}>
            <label style={{ fontSize: 11, display: 'flex', flexDirection: 'column', gap: 2 }}>
              Período (ações em lote) — de
              <InputDataFiltro className={s.input} value={de} onChange={e => setDe(e.target.value)} />
            </label>
            <label style={{ fontSize: 11, display: 'flex', flexDirection: 'column', gap: 2 }}>
              até
              <InputDataFiltro className={s.input} value={ate} onChange={e => setAte(e.target.value)} />
            </label>
            <button className={s.btnErp} onClick={baixarZipLote} disabled={processandoLote}>
              {processandoLote ? 'Compactando...' : '🗜️ Baixar XMLs (.zip)'}
            </button>
            <button className={s.btnErp} onClick={enviarEmailLote} disabled={processandoLote}>
              ✉ Enviar todas por e-mail
            </button>
            <span style={{ fontSize: 11, color: '#666' }}>
              Lista as notas de compra (DF-e) já com XML completo disponível. Para sincronizar com a SEFAZ
              ou manifestar Ciência da Operação, use Compras → Importação DF-e.
            </span>
          </div>

          <table className={s.tabela}>
            <thead><tr>
              <th style={{ width: 110 }}>Emissão</th>
              <th>Emitente</th>
              <th style={{ width: 130 }}>CNPJ</th>
              <th style={{ width: 100, textAlign: 'right' }}>Valor</th>
              <th style={{ width: 170 }}>Ações</th>
            </tr></thead>
            <tbody>
              {carregandoCompra && <tr><td colSpan={5} style={{ textAlign: 'center', color: '#888', padding: 14 }}>Carregando...</td></tr>}
              {!carregandoCompra && docsCompra.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: '#888', padding: 14 }}>Nenhuma nota de compra com XML completo disponível.</td></tr>
              )}
              {docsCompra.map(d => (
                <tr key={d.id} title={d.chave_acesso || ''}>
                  <td>{fmtDT(d.data_emissao)}</td>
                  <td>{d.nome_emitente || '—'}</td>
                  <td>{d.cnpj_emitente || '—'}</td>
                  <td style={{ textAlign: 'right' }}>{fmtMoeda(d.valor)}</td>
                  <td>
                    <button className={s.btnErp} style={{ height: 22, fontSize: 11, padding: '0 8px' }}
                      disabled={ocupadoId === d.id} onClick={() => baixarXmlCompra(d)}>
                      {ocupadoId === d.id ? '...' : '⬇ XML'}
                    </button>
                    <button className={s.btnErp} style={{ height: 22, fontSize: 11, padding: '0 8px', marginLeft: 4 }}
                      onClick={() => enviarEmailCompra(d)}>
                      ✉ E-mail
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {aba === 'nfse' && (
        <>
          <div className={s.toolbar} style={{ flexWrap: 'wrap' }}>
            <select className={s.select} value={statusNfse} onChange={e => setStatusNfse(e.target.value)}
              title="Notas rejeitadas não aparecem aqui — não são documento fiscal de fato; veja-as no Log de Rejeições">
              <option value="">Todos os status</option>
              <option value="autorizada">Autorizada</option>
              <option value="pendente">Pendente</option>
              <option value="cancelada">Cancelada</option>
            </select>
            <input className={s.input} style={{ flex: 1, minWidth: 200 }}
              placeholder="Filtrar por número, chave ou cliente..."
              value={buscaNfse} onChange={e => setBuscaNfse(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && carregarNfse()} />
            <button className={s.btnErp} onClick={carregarNfse}>🔍 Buscar</button>
          </div>

          <table className={s.tabela}>
            <thead><tr>
              <th style={{ width: 110 }}>Emissão</th>
              <th style={{ width: 90 }}>Número</th>
              <th>Serviço</th>
              <th>Cliente</th>
              <th style={{ width: 100, textAlign: 'right' }}>Valor</th>
              <th style={{ width: 100 }}>Status</th>
              <th style={{ width: 90 }}>Ações</th>
            </tr></thead>
            <tbody>
              {carregandoNfse && <tr><td colSpan={7} style={{ textAlign: 'center', color: '#888', padding: 14 }}>Carregando...</td></tr>}
              {!carregandoNfse && docsNfse.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: '#888', padding: 14 }}>Nenhuma NFS-e emitida ainda.</td></tr>
              )}
              {docsNfse.map(d => (
                <tr key={d.id}>
                  <td>{fmtDT(d.data_emissao)}</td>
                  <td>{d.numero}</td>
                  <td title={d.item_descricao || ''}>{d.item_descricao || '—'}</td>
                  <td>{d.cliente_nome || 'Consumidor'}</td>
                  <td style={{ textAlign: 'right' }}>{fmtMoeda(d.item_valor)}</td>
                  <td><span className={`${s.badge} ${STATUS_BADGE[d.status] || s.badgeOutros}`}>{d.status}</span></td>
                  <td>
                    {(d.status === 'autorizada' || d.status === 'cancelada') && (
                      <button className={s.btnErp} style={{ height: 22, fontSize: 11, padding: '0 8px' }}
                        disabled={ocupadoId === d.id} onClick={() => gerarDanfe(d)}>
                        {ocupadoId === d.id ? '...' : '🖨️ DANFE'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <hr style={{ margin: '18px 0' }} />
          <strong style={{ fontSize: 13 }}>Diagnóstico / testes</strong>
          <p style={{ fontSize: 11.5, color: '#666', margin: '4px 0 10px' }}>
            Ferramentas pra conferir a conexão com o Sistema Nacional NFS-e antes de confiar na emissão real.
          </p>
          <div className={s.toolbar} style={{ flexWrap: 'wrap' }}>
            <button className={s.btnErp} onClick={testarNfse} disabled={nfseTestando}>
              {nfseTestando ? 'Consultando…' : '🔎 Testar conexão / Consultar convênio do município'}
            </button>
            <input className={s.input} style={{ width: 160 }}
              placeholder="Ex.: 1.07 (item.subitem)"
              value={nfseCodServico}
              onChange={e => setNfseCodServico(e.target.value)}
              title="Opcional — informe o item.subitem da LC116 (ex. 1.07) ou o cTribNac de 6 dígitos direto; consulta a alíquota do ISS parametrizada pelo município (complementar municipal padrão 000)" />
          </div>

          {nfseErro && <div className={s.erroMsg} style={{ marginTop: 10 }}>{nfseErro}</div>}

          {nfseResultado && (
            <pre style={{
              marginTop: 12, background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 6,
              padding: 12, fontSize: 11.5, overflowX: 'auto', maxHeight: 400,
            }}>
              {JSON.stringify(nfseResultado, null, 2)}
            </pre>
          )}

          <hr style={{ margin: '18px 0' }} />
          <strong style={{ fontSize: 13 }}>Resolver código de tributação nacional (cTribNac) de um produto</strong>
          <p style={{ fontSize: 11.5, color: '#666', margin: '4px 0 8px' }}>
            Verifica se o serviço vinculado ao produto (Cad. Produtos → Serviço LC 116) resolve
            sozinho pro código de 6 dígitos, ou se precisa de escolha explícita porque o subitem
            tem mais de um desdobro nacional possível (tabela oficial, 334 códigos).
          </p>
          <div className={s.toolbar} style={{ flexWrap: 'wrap' }}>
            <input className={s.input} style={{ width: 120 }}
              placeholder="ID do produto"
              value={nfseProdutoId}
              onChange={e => setNfseProdutoId(e.target.value)} />
            <button className={s.btnErp} onClick={resolverCtribnac}>Resolver</button>
          </div>
          {nfseCtribnacErro && <div className={s.erroMsg} style={{ marginTop: 10 }}>{nfseCtribnacErro}</div>}
          {nfseCtribnac && (
            <div style={{ marginTop: 10 }}>
              {nfseCtribnac.ambiguo ? (
                <div style={{ fontSize: 12.5 }}>
                  <span style={{ color: '#b91c1c', fontWeight: 600 }}>Ambíguo — escolha uma opção:</span>
                  <ul style={{ margin: '6px 0 0 18px' }}>
                    {nfseCtribnac.opcoes.map(o => (
                      <li key={o.codigo}><strong>{o.codigo}</strong> — {o.descricao}</li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div style={{ fontSize: 12.5 }}>
                  <span style={{ color: '#166534', fontWeight: 600 }}>Resolvido:</span>{' '}
                  <strong>{nfseCtribnac.cTribNac}</strong> — {nfseCtribnac.opcoes[0]?.descricao}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {aba === 'rejeicoes' && (
        <>
          <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 6, padding: '10px 14px', marginBottom: 14, fontSize: 12.5, color: '#555' }}>
            Nota rejeitada pela SEFAZ/Sistema Nacional não é documento fiscal de fato — nunca existiu
            de verdade pra eles, não tem chave/protocolo válido, não sai pro cliente. Fica separada da
            Central de Notas de propósito; aqui é só auditoria — pra entender o que aconteceu e corrigir
            antes de reemitir (o número costuma ser reaproveitado automaticamente na tentativa seguinte).
          </div>

          <div className={s.toolbar} style={{ flexWrap: 'wrap' }}>
            <select className={s.select} value={tipoRejeicoes} onChange={e => setTipoRejeicoes(e.target.value)}>
              <option value="">Todos os tipos</option>
              <option value="nfce">NFC-e</option>
              <option value="nfe">NF-e</option>
              <option value="nfse">NFS-e</option>
            </select>
            <input className={s.input} style={{ flex: 1, minWidth: 200 }}
              placeholder="Filtrar por número, chave ou cliente..."
              value={buscaRejeicoes} onChange={e => setBuscaRejeicoes(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && carregarRejeicoes()} />
            <button className={s.btnErp} onClick={carregarRejeicoes}>🔍 Buscar</button>
          </div>

          <table className={s.tabela}>
            <thead><tr>
              <th style={{ width: 70 }}>Tipo</th>
              <th style={{ width: 110 }}>Tentativa em</th>
              <th style={{ width: 90 }}>Número</th>
              <th>Cliente / Serviço</th>
              <th style={{ width: 100, textAlign: 'right' }}>Valor</th>
              <th>Motivo da rejeição</th>
            </tr></thead>
            <tbody>
              {carregandoRejeicoes && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#888', padding: 14 }}>Carregando...</td></tr>}
              {!carregandoRejeicoes && docsRejeicoes.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: '#888', padding: 14 }}>Nenhuma rejeição registrada — 🎉</td></tr>
              )}
              {docsRejeicoes.map(d => (
                <tr key={`${d.tipo}-${d.id}`}>
                  <td>{ROTULO_TIPO[d.tipo] || d.tipo?.toUpperCase()}</td>
                  <td>{fmtDT(d.criado_em)}</td>
                  <td>{d.numero}{d.serie ? `/${d.serie}` : ''}</td>
                  <td title={d.item_descricao || ''}>{d.cliente_nome || d.item_descricao || '—'}</td>
                  <td style={{ textAlign: 'right' }}>{fmtMoeda(d.valor ?? d.item_valor)}</td>
                  <td style={{ color: '#b91c1c' }} title={d.motivo_rejeicao || ''}>{d.motivo_rejeicao || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {aba === 'mdfe' && <AbaMdfe />}
      {aba === 'contador' && <AbaContador />}

      {modalEmail && (
        <ModalEmail
          titulo={modalEmail.titulo}
          emailPadrao={emailContador}
          onEnviar={modalEmail.onEnviar}
          onClose={() => setModalEmail(null)}
        />
      )}

      {modalInutilizar && abaVendas && (
        <ModalInutilizacao tipo={aba} onClose={() => setModalInutilizar(false)} />
      )}
    </div>
  );
}
